import { useTimer } from '../hooks/useTimer'
import { TimerDisplay } from './TimerDisplay'
import { TimerControls } from './TimerControls'
import { ProjectTaskSelector } from './ProjectTaskSelector'
import { ProjectTaskBrowser } from './ProjectTaskBrowser'
import { TimerDescriptionInput } from './TimerDescriptionInput'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { AlertTriangle, Minimize2, Maximize2, FolderTree, List, TimerOff } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { useState, useEffect, useRef } from 'react'
import { useWorkspaceSettings } from '@/shared/services/settingsService'
import { useUIStore } from '@/shared/stores/uiStore'
import type { Project, Task } from '@/shared/types/entities'

interface TimerWidgetProps {
  projects?: Project[]
  tasks?: Task[]
  compact?: boolean
}

const TWELVE_HOURS_IN_SECONDS = 12 * 60 * 60

export function TimerWidget({ projects = [], tasks = [], compact }: TimerWidgetProps) {
  const {
    status,
    elapsedSeconds,
    startedAt,
    projectId,
    taskId,
    description,
    isMinimized,
    canStart,
    start,
    pause,
    resume,
    stop,
    cancel,
    setDescription,
    setProjectTask,
    toggleMinimize,
    updateStartedAt,
  } = useTimer()
  const { settings: wsSettings } = useWorkspaceSettings()
  const { addNotification } = useUIStore()
  const autoStopNotifiedRef = useRef(false)

  const [showWarning, setShowWarning] = useState(false)
  const [selectorView, setSelectorView] = useState<'simple' | 'browser'>('simple')

  // Auto-stop timer based on workspace settings
  const autoStopSeconds = (wsSettings?.autoStopTimerHours ?? 0) * 3600

  useEffect(() => {
    if (autoStopSeconds > 0 && elapsedSeconds >= autoStopSeconds && status === 'running') {
      if (!autoStopNotifiedRef.current) {
        autoStopNotifiedRef.current = true
        addNotification({
          type: 'warning',
          message: `Timer auto-stopped after ${wsSettings?.autoStopTimerHours} hours`,
          duration: 5000,
        })
        stop()
      }
    }
  }, [elapsedSeconds, status, autoStopSeconds, stop, addNotification, wsSettings?.autoStopTimerHours])

  // Reset notification flag when timer goes idle
  useEffect(() => {
    if (status === 'idle') {
      autoStopNotifiedRef.current = false
    }
  }, [status])

  useEffect(() => {
    if (elapsedSeconds > TWELVE_HOURS_IN_SECONDS && status === 'running') {
      setShowWarning(true)
    }
  }, [elapsedSeconds, status])

  const handleStart = () => {
    if (projectId) {
      start(projectId, taskId, description)
    }
  }

  const handleProjectChange = (newProjectId: string) => {
    setProjectTask(newProjectId, null)
  }

  const handleTaskChange = (newTaskId: string | null) => {
    if (projectId) {
      setProjectTask(projectId, newTaskId)
    }
  }

  if (compact) {
    return (
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <div className={`h-2 w-2 rounded-full ${status === 'running' ? 'bg-green-500 animate-pulse' : status === 'paused' ? 'bg-amber-400' : 'bg-gray-400'}`} />
          <TimerDisplay elapsedSeconds={elapsedSeconds} startedAt={startedAt} onUpdateStartedAt={updateStartedAt} className="text-lg" isPaused={status === 'paused'} />
        </div>
        <div className="flex items-center gap-1">
          {status === 'idle' ? (
            <Button size="sm" onClick={handleStart} disabled={!canStart}>
              Start
            </Button>
          ) : status === 'running' ? (
            <Button variant="outline" size="sm" onClick={pause}>
              Pause
            </Button>
          ) : (
            <Button size="sm" onClick={resume}>
              Resume
            </Button>
          )}
          {status !== 'idle' && (
            <>
              <Button variant="ghost" size="sm" onClick={stop}>
                Stop
              </Button>
              <Button variant="ghost" size="sm" onClick={cancel} className="text-red-500 hover:text-red-700">
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    )
  }

  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Card className="w-72 shadow-xl border-0 ring-1 ring-gray-200 rounded-xl overflow-hidden">
          {status !== 'idle' && (
            <div className={`h-1 w-full ${status === 'running' ? 'bg-green-500' : 'bg-amber-400'}`} />
          )}
          <CardHeader className="flex flex-row items-center justify-between py-3 px-5">
            <div className="flex items-center gap-2">
              {status !== 'idle' && (
                <div className={`h-2 w-2 rounded-full ${status === 'running' ? 'bg-green-500 animate-pulse' : 'bg-amber-400'}`} />
              )}
              <CardTitle className="text-sm font-medium">
                {status === 'running' ? 'Timer Running' : status === 'paused' ? 'Timer Paused' : 'Timer'}
              </CardTitle>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleMinimize} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="py-3 px-5">
            {status !== 'idle' ? (
              <>
                <TimerDisplay elapsedSeconds={elapsedSeconds} startedAt={startedAt} onUpdateStartedAt={updateStartedAt} className="text-2xl" isPaused={status === 'paused'} />
                <div className="mt-4 flex gap-2">
                  {status === 'running' ? (
                    <Button variant="outline" size="sm" onClick={pause} className="flex-1">
                      Pause
                    </Button>
                  ) : (
                    <Button size="sm" onClick={resume} className="flex-1">
                      Resume
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={stop} className="flex-1 border-green-300 text-green-700 hover:bg-green-50">
                    Stop
                  </Button>
                  <Button variant="outline" size="sm" onClick={cancel} className="flex-1 border-red-200 text-red-600 hover:bg-red-50">
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-2">
                Timer is idle — expand to configure and start
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <Card className="w-full border-0 ring-1 ring-gray-200 shadow-sm rounded-xl overflow-hidden" data-testid="timer-widget">
      {/* Status bar */}
      {status !== 'idle' && (
        <div className={`h-1.5 w-full ${status === 'running' ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-amber-300 to-amber-500'}`} />
      )}

      <CardHeader className="flex flex-row items-center justify-between pb-3 px-6 pt-5">
        <div className="flex items-center gap-2">
          {status !== 'idle' && (
            <div className={`h-2.5 w-2.5 rounded-full ${status === 'running' ? 'bg-green-500 animate-pulse' : 'bg-amber-400'}`} />
          )}
          <CardTitle className="text-lg font-semibold tracking-tight">Timer</CardTitle>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleMinimize} className="h-8 w-8 text-muted-foreground hover:text-foreground" data-testid="minimize-btn">
          <Minimize2 className="h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent className="space-y-5 px-6 pb-6">
        {showWarning && (
          <div className="flex items-center gap-2.5 rounded-lg bg-yellow-50 border border-yellow-200 p-3.5 text-yellow-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="text-sm">Timer has been running for over 12 hours</span>
          </div>
        )}
        {autoStopSeconds > 0 && (
          <div className="flex items-center gap-2 rounded-lg bg-muted/50 border px-3.5 py-2 text-xs text-muted-foreground">
            <TimerOff className="h-3.5 w-3.5 shrink-0" />
            <span>Auto-stops after {wsSettings?.autoStopTimerHours}h</span>
          </div>
        )}

        <TimerDisplay elapsedSeconds={elapsedSeconds} startedAt={startedAt} onUpdateStartedAt={updateStartedAt} className="text-center" isIdle={status === 'idle'} isPaused={status === 'paused'} />

        <Tabs value={selectorView} onValueChange={(v) => setSelectorView(v as 'simple' | 'browser')} className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-lg">
            <TabsTrigger value="simple" className="flex items-center gap-1.5 text-xs">
              <List className="h-3.5 w-3.5" />
              Simple
            </TabsTrigger>
            <TabsTrigger value="browser" className="flex items-center gap-1.5 text-xs">
              <FolderTree className="h-3.5 w-3.5" />
              Browser
            </TabsTrigger>
          </TabsList>

          <TabsContent value="simple" className="mt-3">
            <ProjectTaskSelector
              projects={projects}
              tasks={tasks}
              selectedProjectId={projectId}
              selectedTaskId={taskId}
              onProjectChange={handleProjectChange}
              onTaskChange={handleTaskChange}
              disabled={status !== 'idle'}
            />
          </TabsContent>

          <TabsContent value="browser" className="mt-3">
            <ProjectTaskBrowser
              selectedTaskId={taskId}
              onSelectTask={(task) => {
                if (task) {
                  handleProjectChange(task.projectId)
                  handleTaskChange(task.id)
                }
              }}
              onSelectProject={(projectId) => {
                if (projectId) {
                  handleProjectChange(projectId)
                }
              }}
              onStartTimer={(projectId, taskId, taskName) => {
                if (status === 'idle') {
                  setProjectTask(projectId, taskId)
                  start(projectId, taskId, taskName)
                }
              }}
              showCreateButton={status === 'idle'}
              className="max-h-[400px] border rounded-lg"
            />
          </TabsContent>
        </Tabs>

        <TimerDescriptionInput
          value={description}
          onChange={setDescription}
          disabled={status !== 'idle'}
        />

        <TimerControls
          status={status}
          canStart={canStart}
          onStart={handleStart}
          onPause={pause}
          onResume={resume}
          onStop={stop}
          onCancel={cancel}
        />
      </CardContent>
    </Card>
  )
}

export default TimerWidget
